import { useState, useMemo } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@creanizer/ui'
import { 
  Building2, 
  Search, 
  Filter, 
  Trash2, 
  Edit2, 
  Star, 
  StarOff,
  Copy,
  Link,
  MapPin,
  FolderOpen,
  Download,
  Users,
  Calendar,
  Tag,
  MoreVertical,
  Package,
  Hammer,
  Eye,
  Edit,
  Folder
} from 'lucide-react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { ExportImportButtons } from '../../../../components/common'
import { FolderManager, FolderMoveSelect } from './FolderManager'
import type { BuildingResult, BuildingBatchResult, SavedBuilding } from '@creanizer/types'

interface SavedBuildingsListProps {
  onEdit: (building: BuildingResult) => void
  onBatchView?: (batch: BuildingBatchResult) => void
  onExport?: () => void
  onImport?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function SavedBuildingsList({ onEdit, onBatchView, onExport, onImport }: SavedBuildingsListProps) {
  const { 
    buildings, 
    removeBuilding, 
    updateBuilding,
    linkBuildings,
    unlinkBuildings,
    createDistrict,
    getDistrictBuildings,
    getBuildingsByWorld,
    exportAsJSON,
    exportAsText,
    importBuildings,
    clearAll,
    getStatistics
  } = useBuildingStore()
  
  const { savedWorlds } = useWorldBuilderStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterScale, setFilterScale] = useState<string>('all')
  const [filterWorld, setFilterWorld] = useState<string>('all')
  const [filterFavorite, setFilterFavorite] = useState(false)
  const [editingBuilding, setEditingBuilding] = useState<SavedBuilding | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  // フォルダによるフィルタリング
  let folderFilteredBuildings = buildings
  if (selectedFolderId === 'all') {
    folderFilteredBuildings = buildings
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('building')
    folders.forEach(folder => {
      getItemsInFolder('building', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredBuildings = buildings.filter(building => !allFolderItemIds.has(building.id))
  } else {
    const itemIds = getItemsInFolder('building', selectedFolderId)
    folderFilteredBuildings = buildings.filter(building => itemIds.includes(building.id))
  }
  
  // フィルタリングされた建造物リスト
  const filteredBuildings = useMemo(() => {
    return folderFilteredBuildings.filter(building => {
      // 検索クエリ
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesName = building.result.basicInfo.name.toLowerCase().includes(query)
        const matchesDescription = building.result.basicInfo.description?.toLowerCase().includes(query)
        const matchesMemo = building.memo?.toLowerCase().includes(query)
        const matchesTags = building.tags?.some(tag => tag.toLowerCase().includes(query))
        
        if (!matchesName && !matchesDescription && !matchesMemo && !matchesTags) {
          return false
        }
      }
      
      // タイプフィルタ
      if (filterType !== 'all' && building.result.basicInfo.type !== filterType) {
        return false
      }
      
      // 規模フィルタ
      if (filterScale !== 'all' && building.result.basicInfo.scale !== filterScale) {
        return false
      }
      
      // 世界観フィルタ
      if (filterWorld !== 'all' && building.worldId !== filterWorld) {
        return false
      }
      
      // お気に入りフィルタ
      if (filterFavorite && !building.favorite) {
        return false
      }
      
      return true
    })
  }, [folderFilteredBuildings, searchQuery, filterType, filterScale, filterWorld, filterFavorite])
  
  const handleToggleFavorite = (id: string, current: boolean) => {
    updateBuilding(id, { favorite: !current })
  }
  
  const handleDelete = (id: string) => {
    if (confirm('この建造物を削除しますか？この操作は取り消せません。')) {
      removeBuilding(id)
    }
  }
  
  const getWorldName = (worldId: string | undefined) => {
    if (!worldId) return '未設定'
    const world = savedWorlds.find(w => w.id === worldId)
    return world?.title || '不明'
  }
  
  const stats = getStatistics()
  
  // 単体エクスポート用関数
  const exportSingleAsText = (buildingId: string) => {
    const building = buildings.find(b => b.id === buildingId)
    if (!building) return
    
    let text = `【${building.title}】\n`
    text += `ID: ${building.id}\n`
    text += `作成日: ${new Date(building.createdAt).toLocaleString('ja-JP')}\n\n`
    
    const result = building.result
    text += `■ 基本情報\n`
    text += `名称: ${result.basicInfo.name}\n`
    text += `タイプ: ${result.basicInfo.type}\n`
    text += `規模: ${result.basicInfo.scale}\n`
    text += `説明: ${result.basicInfo.description || '不明'}\n\n`
    
    if (result.exterior) {
      text += `■ 外観\n`
      text += `建材: ${result.exterior.materials?.primary || '不明'}\n`
      if (result.exterior.materials?.secondary?.length) {
        text += `副建材: ${result.exterior.materials.secondary.join('、')}\n`
      }
      text += `外観: ${result.exterior.visualDescription || '不明'}\n\n`
    }
    
    // ダウンロード処理
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `building_${building.id}.txt`
    link.click()
    URL.revokeObjectURL(url)
  }
  
  const exportSingleAsJSON = (buildingId: string) => {
    const building = buildings.find(b => b.id === buildingId)
    if (!building) return
    
    const dataStr = JSON.stringify(building, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', `building_${building.id}.json`)
    linkElement.click()
  }
  
  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済み建造物 ({buildings.length}/{MAX_BUILDINGS})
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成した建造物設定を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="建造物を検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFolders(!showFolders)}
            className="px-2"
          >
            <Folder className="h-4 w-4" />
          </Button>
          <ExportImportButtons
            onExportText={exportAsText}
            onExportJSON={() => {
              if (onExport) {
                onExport()
              } else {
                const data = exportAsJSON()
                const blob = new Blob([data], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `buildings_export_${Date.now()}.json`
                link.click()
                URL.revokeObjectURL(url)
              }
            }}
            onImportJSON={(jsonString) => {
              if (onImport) {
                const file = new File([jsonString], "import.json", { type: "application/json" })
                const event = {
                  target: {
                    files: [file]
                  }
                } as unknown as React.ChangeEvent<HTMLInputElement>
                onImport(event)
              } else {
                try {
                  const data = JSON.parse(jsonString)
                  importBuildings(data)
                } catch (error) {
                  console.error('Import failed:', error)
                  alert('インポートに失敗しました。有効なJSONファイルを選択してください。')
                }
              }
            }}
            dataTypeName="建造物"
          />

          {buildings.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべての建造物を削除しますか？')) {
                  clearAll()
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="building"
                items={buildings}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('building', itemId)
                  moveItemBetweenFolders('building', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* 建造物リスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {filteredBuildings.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    {searchQuery ? '該当する建造物が見つかりません' : 'まだ建造物が保存されていません'}
                  </p>
                ) : (
                  filteredBuildings.map((building) => (
                    <div 
                      key={building.id} 
                      className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move"
                      draggable
                      onDragStart={(e) => {
                        setDraggedItemId(building.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragEnd={() => {
                        setDraggedItemId(null)
                      }}
                      style={{
                        opacity: draggedItemId === building.id ? 0.5 : 1
                      }}
                    >
                  {/* モバイル版: 縦並びレイアウト */}
                  <div className="flex flex-col gap-3 sm:hidden w-full">
                    <div className="w-full">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{building.title}</h3>
                        {building.favorite && <Badge variant="secondary" className="text-xs">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{building.result.basicInfo.type}</Badge>
                        <Badge variant="outline" className="text-xs">{building.result.basicInfo.scale}</Badge>
                        {building.worldId && (
                          <Badge variant="secondary" className="text-xs">{getWorldName(building.worldId)}</Badge>
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        {building.result.basicInfo.description || building.result.basicInfo.summary}
                      </p>
                      
                      {building.tags && building.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {building.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="px-2 py-1 bg-primary/10 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(building.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2 w-48">
                        <FolderMoveSelect
                          generatorType="building"
                          itemId={building.id}
                          currentFolderId={getItemFolder('building', building.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(building.result)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingBuilding(building)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(building.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(building.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(building.id)}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm truncate">{building.title}</h3>
                        {building.favorite && <Badge variant="secondary">お気に入り</Badge>}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Badge variant="outline">{building.result.basicInfo.type}</Badge>
                        <Badge variant="outline">{building.result.basicInfo.scale}</Badge>
                        {building.worldId && (
                          <Badge variant="secondary">{getWorldName(building.worldId)}</Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {building.result.basicInfo.description || building.result.basicInfo.summary}
                      </p>
                      
                      {building.tags && building.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {building.tags.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(building.createdAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {/* フォルダ選択 */}
                      <div className="mt-2 w-48">
                        <FolderMoveSelect
                          generatorType="building"
                          itemId={building.id}
                          currentFolderId={getItemFolder('building', building.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(building.result)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(building.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingBuilding(building)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(building.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleFavorite(building.id, building.favorite || false)}
                        >
                          {building.favorite ? (
                            <>
                              <Star className="h-4 w-4 mr-2 fill-current text-yellow-500" />
                              解除
                            </>
                          ) : (
                            <>
                              <StarOff className="h-4 w-4 mr-2" />
                              お気に入り
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(building.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          削除
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  )
}

const MAX_BUILDINGS = 100